import React, { useState, useEffect } from 'react';
import { X, Search, RefreshCw, BookmarkCheck } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { multipatrocinadorService } from '../services/multipatrocinadores';
import { PromocaoCard } from './PromocaoCard';
import { PaginacaoControls } from './PaginacaoControls';
import { LoadingSpinner } from './LoadingSpinner';

interface SavedPromocoesListProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectPromocao: (promocao: any) => void;
}

export const SavedPromocoesList: React.FC<SavedPromocoesListProps> = ({
  isOpen,
  onClose,
  onSelectPromocao,
}) => {
  const [savedPromocoes, setSavedPromocoes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 50,
    offset: 0,
    count: 0,
  });

  const loadSavedPromocoes = async (search: string = '', offset: number = 0) => {
    setLoading(true);
    try {
      const response = await multipatrocinadorService.getSavedPromocoes(search, offset, 50);
      if (response.success) {
        setSavedPromocoes(response.data);
        setPagination(response.pagination);
      }
    } catch (error) {
      console.error('Erro ao carregar promoções salvas:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadSavedPromocoes();
    }
  }, [isOpen]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    loadSavedPromocoes(searchTerm, 0);
  };

  const handlePaginaChange = (pagina: number) => {
    const newOffset = (pagina - 1) * pagination.limit;
    loadSavedPromocoes(searchTerm, newOffset);
  };

  const paginaAtual = Math.floor(pagination.offset / pagination.limit) + 1;
  const totalPaginas = Math.ceil(pagination.total / pagination.limit);

  const handleRefresh = () => {
    loadSavedPromocoes(searchTerm, pagination.offset);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <BookmarkCheck className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Promoções Editadas</h2>
                <p className="text-blue-100 text-sm">
                  {pagination.total} {pagination.total === 1 ? 'promoção encontrada' : 'promoções encontradas'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors disabled:opacity-50"
                title="Atualizar"
              >
                <RefreshCw className={`h-5 w-5 text-white ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="h-6 w-6 text-white" />
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <form onSubmit={handleSearch} className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar promoção editada..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-all shadow-lg shadow-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Buscar
              </button>
            </form>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <LoadingSpinner />
              </div>
            ) : savedPromocoes.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="w-20 h-20 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
                  <BookmarkCheck className="h-10 w-10 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Nenhuma promoção editada
                </h3>
                <p className="text-gray-500 max-w-md">
                  {searchTerm
                    ? 'Não encontramos promoções editadas com esse termo de busca.'
                    : 'Você ainda não editou nenhuma promoção. Edite uma promoção para vê-la aqui.'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                {savedPromocoes.map((promocao) => (
                  <PromocaoCard
                    key={promocao.IDRegraPromocao}
                    promocao={promocao}
                    selecionada={false}
                    onSelecionar={onSelectPromocao}
                    onViewHistory={()=>{}}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Footer - Pagination */}
          {!loading && savedPromocoes.length > 0 && totalPaginas > 1 && (
            <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
              <PaginacaoControls
                paginaAtual={paginaAtual}
                totalPaginas={totalPaginas}
                totalItens={pagination.total}
                itensPorPagina={pagination.limit}
                onPaginaChange={handlePaginaChange}
                variant="completa"
              />
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
